import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiDroplet, 
  FiScissors, 
  FiMinimize2, 
  FiRefreshCw, 
  FiFileText, 
  FiCpu,
  FiZap,
  FiShield,
  FiCloud,
  FiStar
} from 'react-icons/fi';
import FeatureCard from '../components/FeatureCard';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <FiDroplet />,
      title: 'Watermark Removal',
      description: 'Remove unwanted watermarks from your images with our advanced AI technology. Clean and professional results every time.',
      gradient: 'primary',
      path: '/watermark-removal'
    },
    {
      icon: <FiScissors />,
      title: 'Background Removal',
      description: 'Instantly remove backgrounds from any image. Perfect for product photos, portraits, and creative projects.',
      gradient: 'secondary',
      path: '/background-removal'
    },
    {
      icon: <FiMinimize2 />,
      title: 'Image Compression',
      description: 'Reduce file sizes without losing quality. Optimize your images for web, email, or storage.',
      gradient: 'success',
      path: '/compress'
    },
    {
      icon: <FiRefreshCw />,
      title: 'Format Conversion',
      description: 'Convert between JPG, PNG, WebP, GIF, and more. Fast, free, and easy to use.',
      gradient: 'accent',
      path: '/convert'
    },
    {
      icon: <FiFileText />,
      title: 'Image to PDF',
      description: 'Convert single or multiple images into a PDF document. Perfect for documents, presentations, and portfolios.',
      gradient: 'warning',
      path: '/image-to-pdf'
    },
    {
      icon: <FiCpu />,
      title: 'AI Image Generation',
      description: 'Create stunning images from text descriptions using state-of-the-art AI. Bring your imagination to life.',
      gradient: 'dark',
      path: '/ai-generate'
    }
  ];

  const stats = [
    { number: '10M+', label: 'Images Processed' },
    { number: '500K+', label: 'Happy Users' },
    { number: '99.9%', label: 'Uptime' },
    { number: '< 5s', label: 'Average Speed' }
  ];

  const benefits = [
    {
      icon: <FiZap />,
      title: 'Lightning Fast',
      description: 'Process images in seconds with our optimized AI infrastructure.'
    },
    {
      icon: <FiShield />,
      title: 'Secure & Private',
      description: 'Your images are processed securely and never stored on our servers.'
    },
    {
      icon: <FiCloud />,
      title: 'No Installation',
      description: 'Works entirely in your browser. No software to download or install.'
    },
    {
      icon: <FiStar />,
      title: 'Free to Use',
      description: 'Access all features for free. No hidden fees or subscriptions.'
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-pattern"></div>
        </div>
        
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="hero-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <span className="badge-icon">✨</span>
              <span>AI-Powered Image Tools</span>
            </motion.div>
            
            <h1 className="hero-title">
              Transform Your Images with
              <span className="gradient-text"> AI Magic</span>
            </h1>
            
            <p className="hero-subtitle">
              Professional image processing tools powered by artificial intelligence.
              Remove watermarks, backgrounds, compress, convert, and generate images — all in one place.
            </p>
            
            <div className="hero-actions">
              <motion.button 
                className="btn-primary"
                onClick={() => navigate('/watermark-removal')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started Free
              </motion.button>
              <motion.button 
                className="btn-secondary"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Explore Features
              </motion.button>
            </div>
          </motion.div>

          <motion.div 
            className="hero-image"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="hero-mockup">
              <div className="mockup-header">
                <div className="mockup-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="mockup-content">
                <div className="mockup-preview">
                  <div className="preview-before">
                    <span>Before</span>
                    <div className="preview-placeholder">
                      <FiDroplet size={40} />
                    </div>
                  </div>
                  <div className="preview-arrow">→</div>
                  <div className="preview-after">
                    <span>After</span>
                    <div className="preview-placeholder clean">
                      <FiStar size={40} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="floating-cards">
              <motion.div 
                className="floating-card card-1"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <FiZap /> Processing...
              </motion.div>
              <motion.div 
                className="floating-card card-2"
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
              >
                <FiShield /> 100% Secure
              </motion.div>
              <motion.div 
                className="floating-card card-3"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
              >
                <FiStar /> AI Powered
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div 
                key={stat.label}
                className="stat-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" id="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">
              Powerful Tools for
              <span className="gradient-text"> Every Need</span>
            </h2>
            <p className="section-subtitle">
              Everything you need to edit, enhance, and transform your images — all powered by cutting-edge AI technology.
            </p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                gradient={feature.gradient}
                onClick={() => navigate(feature.path)}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">
              Why Choose
              <span className="gradient-text"> ImageCoCo?</span>
            </h2>
            <p className="section-subtitle">
              We've built the fastest, most reliable image processing platform with your needs in mind.
            </p>
          </motion.div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={benefit.title}
                className="benefit-item"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Transform Your Images?</h2>
            <p>Start using our AI-powered tools for free. No sign-up required.</p>
            <motion.button 
              className="btn-cta"
              onClick={() => navigate('/watermark-removal')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started Now
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
